import { Button } from "@/Button";
import React, { useState, useMemo } from "react";
import chevironLeft from "$/cheviron-left.svg";
import chevironRight from "$/cheviron-right.svg";
import { twMerge } from "tailwind-merge";
import { useBusinessServiceStats } from "~/services/business/myPage";

interface ServiceIncomeItem {
  productTicketSeq: number;
  name: string;
  percentage: number;
  amount: number;
  color: string;
}

interface PetRanking {
  rank: number;
  name: string;
  amount: number;
}

const Statistics: React.FC = () => {
  // 현재 날짜 구하기
  const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}.${month}`;
  };

  const [selectedDate, setSelectedDate] = useState(getCurrentDate());

  // selectedDate를 YYYYMM 형식으로 변환
  const yearMonth = useMemo(() => {
    return selectedDate.replace(".", "");
  }, [selectedDate]);

  // API 호출
  const {
    data: statsData,
    isLoading,
    error,
  } = useBusinessServiceStats({
    yearMonth: yearMonth,
  });

  // 이전달로 이동
  const handlePrev = () => {
    const [year, month] = selectedDate.split(".").map(Number);
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    setSelectedDate(`${prevYear}.${String(prevMonth).padStart(2, "0")}`);
  };

  // 다음달로 이동
  const handleNext = () => {
    const [year, month] = selectedDate.split(".").map(Number);
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    setSelectedDate(`${nextYear}.${String(nextMonth).padStart(2, "0")}`);
  };

  // 현재 달인지 확인
  const currentDate = getCurrentDate();
  const isCurrentMonth = selectedDate === currentDate;

  // tickets 데이터를 이용해 이번 달 수입 구성 (delYn 무관 전체 표시)
  const serviceIncomeData: ServiceIncomeItem[] = useMemo(() => {
    if (!statsData?.data?.tickets) return [];

    const tickets = [...statsData.data.tickets].sort(
      (a, b) => (a.dataOrder ?? 0) - (b.dataOrder ?? 0),
    );

    const totalFromTickets = tickets.reduce(
      (sum, t) => sum + (t.totalAmount ?? 0),
      0,
    );

    return tickets.map((ticket) => {
      const amount = ticket.totalAmount ?? 0;
      const percentage =
        totalFromTickets > 0
          ? Math.round((amount / totalFromTickets) * 1000) / 10
          : (ticket.salesRate ?? 0);

      return {
        productTicketSeq: ticket.productTicketSeq,
        name: ticket.name,
        percentage,
        amount,
        color: "#FF6B6B",
      };
    });
  }, [statsData]);

  // tickets 기반 합계 (serviceIncomeData에서 파생)
  const totalAmount = useMemo(
    () => serviceIncomeData.reduce((sum, item) => sum + item.amount, 0),
    [serviceIncomeData],
  );

  const petRankingData: PetRanking[] = useMemo(() => {
    if (!statsData?.data?.salesRanks) return [];

    return statsData.data.salesRanks.map((rank) => {
      const salesAmount = rank.salesAmount;
      const amount =
        typeof salesAmount === "number"
          ? salesAmount
          : Number.parseInt(String(salesAmount).replace(/\D/g, ""), 10) || 0;
      return {
        rank: rank.no,
        name: rank.petName,
        amount,
      };
    });
  }, [statsData]);

  // 로딩 상태 처리
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-4 bg-gray-50">
        <div className="text-center">
          <div className="text-lg font-medium text-gray-600">
            데이터를 불러오는 중...
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태 처리
  if (error) {
    return (
      <div className="flex justify-center items-center p-4 bg-gray-50">
        <div className="text-center">
          <div className="text-lg font-medium text-red-600">
            데이터를 불러오는데 실패했습니다.
          </div>
          <div className="mt-2 text-sm text-gray-500">
            잠시 후 다시 시도해주세요.
          </div>
        </div>
      </div>
    );
  }

  const formatNumber = (num: number) => {
    return num.toLocaleString("ko-KR");
  };

  return (
    <div className="w-full min-w-0 max-w-[100vw] overflow-x-hidden p-4 bg-gray-50">
      <div className="mx-auto w-full rounded-lg sm:max-w-md">
        {/* 날짜 헤더 */}
        <div className="flex justify-between items-center px-4 text-center bg-gray-100 rounded-t-lg">
          <div className="flex justify-center items-center w-10 h-10">
            <Button onClick={handlePrev} className={twMerge("bg-[#F5F5F5]")}>
              <img src={chevironLeft} alt="이전 달력" />
            </Button>
          </div>
          <div className="flex-1 text-lg font-semibold text-center text-gray-800">
            {selectedDate}
          </div>
          <div className="flex justify-center items-center w-10 h-10">
            <Button
              onClick={handleNext}
              className={twMerge(
                "bg-[#F5F5F5]",
                isCurrentMonth && "opacity-0 pointer-events-none",
              )}
              disabled={isCurrentMonth}
            >
              <img src={chevironRight} alt="다음 달력" />
            </Button>
          </div>
        </div>

        {/* 서비스 이용 현황 */}
        <div className="grid grid-cols-2 gap-4 py-4 min-w-0">
          <div className="bg-gray-50 p-4 rounded-lg text-center border border-[#E9E9E9] bg-white">
            <p className="mb-2 text-sm text-gray-600">이번 달 서비스 이용</p>
            <p className="text-2xl font-bold text-gray-800">
              {statsData?.data?.usingPetCount || 0}{" "}
              <span className="text-lg font-normal">마리</span>
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg text-center border border-[#E9E9E9] bg-white">
            <p className="mb-2 text-sm text-gray-600">유치원 재등록률</p>
            <p className="text-2xl font-bold text-gray-800">
              {statsData?.data?.reorderRate || 0}{" "}
              <span className="text-lg font-normal">%</span>
            </p>
          </div>
        </div>

        {/* 이번 달 수입 */}
        <div className="min-w-0 overflow-hidden p-4 border-[1px] border-[#E9E9E9] bg-white rounded-lg">
          <h3 className="mb-4 font-semibold text-gray-800">이번 달 수입</h3>

          {/* 서비스별 수입 목록 */}
          <div className="mb-4 space-y-3">
            {serviceIncomeData.map((item) => (
              <div
                key={item.productTicketSeq}
                className="flex justify-between items-center min-w-0"
              >
                <div className="flex max-w-[200px] min-w-0 items-center overflow-hidden">
                  <span className="text-sm font-medium text-gray-700 truncate">
                    {item.name}
                  </span>
                </div>
                <div className="flex gap-2 justify-end items-center shrink-0">
                  <span className="w-10 text-sm text-right text-gray-500">
                    {item.percentage}%
                  </span>
                  <span className="min-w-[70px] text-right text-sm font-medium text-gray-800">
                    {formatNumber(item.amount)} 원
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* 합계 */}
          <div className="pt-3 border-t border-black">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-800">합계</span>
              <span className="min-w-[70px] shrink-0 text-right text-lg font-bold text-gray-800">
                {formatNumber(totalAmount)} 원
              </span>
            </div>
          </div>

          {/* 수익률 관련 공지사항 */}
          <div className="p-3 mt-4 bg-blue-50 border-t border-black">
            <p className="text-xs text-gray-600 text-center text-[#666666]">
              수익률 전체 결제 금액에서 수수료를 제외한 금액을 표시합니다.
            </p>
          </div>
        </div>

        {/* 반려견 별 매출 순위 */}
        <div className="min-w-0 overflow-hidden p-4 border-[1px] border-[#E9E9E9] bg-white rounded-lg mt-4">
          <h3 className="mb-2 font-semibold text-gray-800">
            반려견 별 매출 순위
          </h3>
          <div className="space-y-1">
            {petRankingData.map((pet) => (
              <div
                key={pet.rank}
                className="flex justify-between items-center py-2 min-w-0"
              >
                <div className="flex max-w-[45%] min-w-0 items-center overflow-hidden">
                  <span className="w-6 text-sm font-medium text-gray-600 shrink-0">
                    {pet.rank}
                  </span>
                  <span className="ml-4 min-w-0 text-sm font-medium text-gray-800 truncate">
                    {pet.name}
                  </span>
                </div>
                <span className="min-w-[70px] shrink-0 text-right text-sm font-medium text-gray-800">
                  {formatNumber(pet.amount)} 원
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
